import React, { useMemo, useState } from 'react';
import { getCollection } from '../game/meta/progression';
import { getModelById } from '../game/voxel/models';
import { publicShellPayload } from '../game/voxel/slicePipeline';
import HologramViewport from './HologramViewport';
import { IconMuseum } from './AtelierIcons';

function formatUnlocked(ts) {
    if (!ts) return null;
    try {
        return new Date(ts).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return null;
    }
}

const CollectionView = ({ onClose }) => {
    const items = useMemo(() => getCollection(), []);
    const [selected, setSelected] = useState(items[0] || null);
    const [filter, setFilter] = useState('ALL');
    const model = selected ? getModelById(selected.modelId) : null;
    const shell = model ? publicShellPayload(model, null) : null;
    const unlockedLabel = formatUnlocked(selected?.unlockedAt);

    const categories = useMemo(() => {
        const set = new Set(items.map((it) => it.category).filter(Boolean));
        return Array.from(set);
    }, [items]);

    const visible = useMemo(() => {
        if (filter === 'ALL') return items;
        return items.filter((it) => it.category === filter);
    }, [items, filter]);

    return (
        <div className="atelier-overlay atelier-collection" role="dialog" aria-label="Relic Museum">
            <div className="atelier-overlay-shell">
                <header className="atelier-overlay-top">
                    <div className="atelier-brand atelier-brand--compact">
                        <span className="atelier-brand-mark" aria-hidden>
                            <IconMuseum />
                        </span>
                        <div>
                            <div className="atelier-brand-name">Voxel Atelier</div>
                            <div className="atelier-brand-sub">Relic Museum</div>
                        </div>
                    </div>
                    <button type="button" className="atelier-chip-btn" onClick={onClose}>
                        Done
                    </button>
                </header>

                <h1 className="atelier-overlay-title">Relic Museum</h1>
                <p className="atelier-overlay-sub">
                    Curated specimens restored by the syndicate
                </p>

                <div className="atelier-seg" role="tablist" aria-label="Collection filter">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={filter === 'ALL'}
                        className={`atelier-seg-btn${filter === 'ALL' ? ' is-active' : ''}`}
                        onClick={() => setFilter('ALL')}
                    >
                        All ({items.length})
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            type="button"
                            role="tab"
                            aria-selected={filter === cat}
                            className={`atelier-seg-btn${filter === cat ? ' is-active' : ''}`}
                            onClick={() => setFilter(cat)}
                        >
                            {String(cat).charAt(0).toUpperCase() + String(cat).slice(1)}
                        </button>
                    ))}
                </div>

                {items.length === 0 ? (
                    <div className="atelier-empty-panel">
                        <div className="atelier-empty-glyph">?</div>
                        <h2>No relics yet</h2>
                        <p>
                            Contribute at least one slice or a correct blind guess to unlock
                            today&apos;s object.
                        </p>
                    </div>
                ) : (
                    <>
                        {shell && model && (
                            <section className="atelier-specimen" aria-label="Selected specimen">
                                <div className="atelier-specimen-tag">RESTORED SPECIMEN</div>
                                <div className="atelier-specimen-stage">
                                    <HologramViewport
                                        size={shell.size}
                                        voxels={shell.voxels}
                                        palette={shell.palette}
                                        grayscale={false}
                                        autoRotate
                                        presentation="trophy"
                                        height={220}
                                    />
                                    <div className="atelier-specimen-hint">Drag to rotate 360°</div>
                                </div>
                                <div className="atelier-specimen-meta">
                                    <h2 className="atelier-specimen-name">{model.name || selected.name}</h2>
                                    <p className="atelier-specimen-desc">
                                        {selected.category || model.category || 'Archive'}
                                        {unlockedLabel ? ` · Restored ${unlockedLabel}` : ''}
                                    </p>
                                </div>
                            </section>
                        )}

                        <section className="atelier-relic-grid" aria-label="Curated specimens">
                            <h3 className="atelier-section-label">Curated Specimens</h3>
                            <div className="atelier-relic-cards">
                                {visible.map((it) => {
                                    const active =
                                        selected?.modelId === it.modelId &&
                                        selected?.date === it.date;
                                    const dateLabel = formatUnlocked(it.unlockedAt);
                                    return (
                                        <button
                                            key={it.modelId + (it.date || '')}
                                            type="button"
                                            className={`atelier-relic-card${active ? ' is-active' : ''}`}
                                            onClick={() => setSelected(it)}
                                            aria-pressed={active}
                                        >
                                            <span className="atelier-relic-mono" aria-hidden>
                                                {(it.name || '?').slice(0, 1)}
                                            </span>
                                            <span className="atelier-relic-copy">
                                                <span className="atelier-relic-name">{it.name}</span>
                                                <span className="atelier-relic-date">
                                                    {dateLabel
                                                        ? dateLabel
                                                        : it.category || 'Archive'}
                                                </span>
                                            </span>
                                            <span className="atelier-relic-pct" title="Fully restored">
                                                <span className="atelier-relic-check">✓</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    </>
                )}

                <aside className="atelier-mandate">
                    <div className="atelier-mandate-kicker">Restoration mandate</div>
                    <p>
                        Solve daily slices or land a valid blind guess to carve and preserve relics
                        in the museum forever.
                    </p>
                </aside>
            </div>
        </div>
    );
};

export default CollectionView;
